import MedicalReportPrompt from "@/data/interfaces/MedicalReportPrompt";
import MedicalReportPromptType from "@/data/interfaces/MedicalReportPromptType";
import adminService from "@/services/adminService";
import { useEffect, useState } from "react";
import { ErrorMessage } from "@/components/UI";
import TypeForm from "../Shared/PromptManagement/TypeForm";
import TypeList from "../Shared/PromptManagement/TypeList";
import PromptForm from "../Shared/PromptManagement/PromptForm";
import PromptList from "../Shared/PromptManagement/PromptList";
import { Circle, CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { adminStatusBadgeClass } from "../../Shared/adminUi";
import { Button } from "@/components/UI/Buttons/Button";

type FieldValue = string | number | File;

const MedicalReportPrompts = () => {
  const [promptTypes, setPromptTypes] = useState<
    MedicalReportPromptType[] | undefined
  >();
  const [selectedType, setSelectedType] = useState<
    MedicalReportPromptType | undefined
  >();
  const [prompts, setPrompts] = useState<MedicalReportPrompt[] | undefined>();

  const [editingPrompt, setEditingPrompt] = useState<
    MedicalReportPrompt | undefined
  >();
  const [editingPromptType, setEditingPromptType] = useState<
    MedicalReportPromptType | undefined
  >();

  const [errorMessage, setErrorMsg] = useState<string>();

  useEffect(() => {
    loadPromptTypes();
  }, []);

  useEffect(() => {
    if (selectedType) {
      loadPrompts(selectedType.id);
    }
  }, [selectedType]);

  const loadPromptTypes = async () => {
    try {
      const types = await adminService.medicalReportPromptType.getAll();
      setPromptTypes(types);
    } catch (error) {
      console.error(error);
      setErrorMsg(`NEPAVYKO GAUTI TIPŲ: ${error}`);
    }
  };

  const addNewType = async (data: Record<string, FieldValue>) => {
    try {
      setErrorMsg(undefined);
      await adminService.medicalReportPromptType.create(String(data.name));
      loadPromptTypes();
    } catch (error) {
      console.error(error);
      setErrorMsg(`NEPAVYKO PRIDĖTI NAUJO TIPO: ${error}`);
    }
  };

  const updatePromptType = async (promptType: MedicalReportPromptType) => {
    try {
      setErrorMsg(undefined);
      await adminService.medicalReportPromptType.update(promptType);
      setEditingPromptType(undefined);
      loadPromptTypes();
    } catch (error) {
      console.error(error);
      setErrorMsg(`NEPAVYKO ATNAUJINTI TIPĄ: ${error}`);
    }
  };

  const setDefaultType = async (type: MedicalReportPromptType) => {
    updatePromptType({
      ...type,
      is_default: true,
    });
  };

  const deletePromptType = async (type: MedicalReportPromptType) => {
    try {
      setErrorMsg(undefined);
      await adminService.medicalReportPromptType.delete(type.id);
      loadPromptTypes();
    } catch (error) {
      console.error(error);
      setErrorMsg(`NEPAVYKO IŠTRINTI TIPO: ${error}`);
    }
  };

  const loadPrompts = async (typeId: number) => {
    try {
      const promptsData =
        await adminService.medicalReportPrompt.getByType(typeId);
      setPrompts(promptsData);
    } catch (error) {
      console.error(error);
      setErrorMsg(`NEPAVYKO GAUTI PROMPTŲ: ${error}`);
    }
  };

  const addNewPrompt = async (name: string, content: string) => {
    if (!selectedType) return;

    try {
      setErrorMsg(undefined);
      await adminService.medicalReportPrompt.create(
        selectedType.id,
        name,
        content,
      );
      loadPrompts(selectedType.id);
    } catch (error) {
      console.error(error);
      setErrorMsg(`NEPAVYKO PRIDĖTI NAUJO PROMPTO: ${error}`);
    }
  };

  const updatePrompt = async (prompt: MedicalReportPrompt) => {
    try {
      setErrorMsg(undefined);
      await adminService.medicalReportPrompt.update(prompt);
      setEditingPrompt(undefined);

      if (!selectedType) return;

      loadPrompts(selectedType.id);
    } catch (error) {
      console.error(error);
      setErrorMsg(`NEPAVYKO ATNAUJINTI PROMPTĄ: ${error}`);
    }
  };

  const deletePrompt = async (prompt: MedicalReportPrompt) => {
    try {
      setErrorMsg(undefined);
      await adminService.medicalReportPrompt.delete(prompt.id);

      if (!selectedType) return;

      loadPrompts(selectedType.id);
    } catch (error) {
      console.error(error);
      setErrorMsg(`NEPAVYKO IŠTRINTI PROMPTĄ: ${error}`);
    }
  };

  return (
    <>
      <div className="flex flex-col-reverse gap-8 md:flex-row md:gap-4">
        <div className="flex flex-col gap-2 md:w-fit md:min-w-[200px] lg:min-w-[350px]">
          {!selectedType ? (
            <TypeForm
              onSubmit={addNewType}
              onUpdate={updatePromptType}
              editingType={editingPromptType}
              onCancelEdit={() => setEditingPromptType(undefined)}
              title="Pridėti naują tipą:"
            />
          ) : (
            <PromptForm
              selectedType={selectedType}
              onSubmit={addNewPrompt}
              onUpdate={updatePrompt}
              onBack={() => setSelectedType(undefined)}
              editingPrompt={editingPrompt}
              onCancelEdit={() => setEditingPrompt(undefined)}
            />
          )}

          <ErrorMessage error={errorMessage} />
        </div>

        {!selectedType ? (
          <TypeList
            types={promptTypes}
            onSelectType={setSelectedType}
            onDeleteType={deletePromptType}
            onEditType={setEditingPromptType}
            title="Medical Report Prompt Tipai"
            renderTypeBadges={(type) => (
              <span className={adminStatusBadgeClass(!!type.is_active)}>
                {type.is_active ? "Aktyvus" : "Neaktyvus"}
              </span>
            )}
            extraActions={(type) => (
              <Button
                size="sm"
                variant="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  setDefaultType(type);
                }}
                className={cn(
                  "border-line-strong shrink-0 border",
                  type.is_default
                    ? "border-green/30 bg-green/12 text-green"
                    : "bg-panel text-muted hover:bg-surface-hover hover:text-ink",
                )}
              >
                {type.is_default ? <CheckCircle /> : <Circle />}
              </Button>
            )}
          />
        ) : (
          <PromptList
            prompts={prompts}
            selectedType={selectedType}
            onDeletePrompt={deletePrompt}
            onEditPrompt={setEditingPrompt}
            title="Medical Report Prompts"
            renderPromptBadges={(prompt) => (
              <span className={adminStatusBadgeClass(!!prompt.is_active)}>
                {prompt.is_active ? "Aktyvus" : "Neaktyvus"}
              </span>
            )}
          />
        )}
      </div>
    </>
  );
};

export default MedicalReportPrompts;
